import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { action, setProperties } from '@ember/object';
import { inject as service } from '@ember/service';
import { restartableTask, task, timeout } from 'ember-concurrency';
import { isPresent } from '@ember/utils';
import DS from 'ember-data';

import AnalyticsService from 'cio/services/analytics';
import ApiErrorsService from 'cio/services/api-errors';
import ApiService from 'cio/services/api';
import ObjectTypesService from 'cio/services/object-types';
import config from 'cio/config/environment';
import { SortDirection, sortByCaseInsensitive } from 'cio/utils/sort';
import SnippetModel from 'cio/models/snippet';

export enum SortProperty {
  Name = 'name',
  UpdatedAt = 'updatedAt',
  Value = 'value',
}

const DEFAULT_VALUES = {
  page: 1,
  search: '',
  sortBy: SortProperty.UpdatedAt,
  sortDirection: SortDirection.Descending,
  objectTypeIds: [],
};

const ROWS_PER_PAGE = 20;

export default class SnippetsIndexController extends Controller {
  @service analytics!: AnalyticsService;
  @service apiErrors!: ApiErrorsService;
  @service api!: ApiService;
  @service objectTypes!: ObjectTypesService;
  @service store!: DS.Store;

  SortDirection = SortDirection;

  queryParams = ['page', 'search', 'sortBy', 'sortDirection', 'objectTypeIds'];
  perPage = ROWS_PER_PAGE;
  trackListSortedEvent = false;
  emptySearchImage = new URL('cio/assets/images/illustrations/empty-search.png', import.meta.url).toString();

  @tracked page = DEFAULT_VALUES.page;
  @tracked search = DEFAULT_VALUES.search;
  @tracked sortBy = DEFAULT_VALUES.sortBy;
  @tracked sortDirection = DEFAULT_VALUES.sortDirection;
  @tracked objectTypeIds: string[] = DEFAULT_VALUES.objectTypeIds;
  @tracked objectTypeSnippets: SnippetModel[] | null = null;

  readonly tableHeaders = [
    { label: 'Name', value: 'name', class: 'w-[250px]' },
    { label: 'Value', value: 'value' },
    { label: 'Liquid' },
    { label: 'Updated', value: 'updated_at', class: 'w-[175px]' },
  ];

  get snippets() {
    if (this.objectTypeSnippets != null) {
      let searchTerm = this.search.toLowerCase();
      let filteredSnippets = this.objectTypeSnippets.filter(
        (s) => s.name.toLowerCase().includes(searchTerm) || s.value.toLowerCase().includes(searchTerm),
      );
      let sortedSnippets = sortByCaseInsensitive(filteredSnippets, [
        this.sortBy === SortProperty.UpdatedAt ? 'updatedAt' : this.sortBy,
      ]);
      return this.sortDirection === SortDirection.Descending ? sortedSnippets.reverse() : sortedSnippets;
    }

    return this.fetchSnippets.last?.value ?? [];
  }

  get total() {
    return (
      (this.objectTypeSnippets != null
        ? this.objectTypeSnippets.length
        : (this.fetchSnippets.lastSuccessful?.value as any)?.meta?.pagination.total) ?? 0
    );
  }

  get pageSize() {
    return this.objectTypeSnippets != null ? this.objectTypeSnippets.length : this.perPage;
  }

  get isSearchActive() {
    return isPresent(this.objectTypeSnippets) || isPresent(this.search);
  }

  setDefaults() {
    setProperties(this, DEFAULT_VALUES);
  }

  resetPage() {
    this.page = DEFAULT_VALUES.page;
  }

  @action
  setSearch(search: string) {
    this.search = search;
    this.fetchSearch.perform();
  }

  @action
  setObjectTypeIds(objectTypeIds: string[]) {
    this.objectTypeIds = objectTypeIds;
    this.resetPage();

    if (objectTypeIds.length > 0) {
      this.fetchObjectTypeSnippets.perform(objectTypeIds);
    } else {
      this.objectTypeSnippets = null;
    }

    this.analytics.track('snippets', 'list_filtered', {
      objectTypeIds,
      objectTypes: this.store.peekAll('object-type'),
    });
  }

  @action
  setSort(sortBy: SortProperty, sortDirection: SortDirection) {
    this.sortBy = sortBy;
    this.sortDirection = sortDirection;
    this.trackListSortedEvent = true;

    this.resetPage();
  }

  fetchObjectTypeSnippets = restartableTask(async (objectTypeIds: string[]) => {
    let queryString = objectTypeIds.map((id) => `objectTypeIds[]=${id}`).join('&');
    try {
      let { snippets } = await this.api.request(`object_type_snippets?${queryString}`);
      this.objectTypeSnippets = snippets;
    } catch {
      this.objectTypeSnippets = null;
    }
  });

  fetchSearch = task({ restartable: true }, async () => {
    await timeout(config.APP.debounceDuration);
    this.resetPage();
  });

  tryFetchSnippets = task({ restartable: true }, async () => {
    if (this.objectTypeIds.length > 0) {
      return;
    }

    this.fetchSnippets.perform();
  });

  fetchSnippets = task({ restartable: true }, async () => {
    try {
      let snippets = await this.store.query('snippet', {
        asc: this.sortDirection === SortDirection.Ascending,
        page: this.page,
        search: this.search,
        sort: this.sortBy,
        limit: this.perPage,
      });

      if (this.trackListSortedEvent) {
        this.analytics.track('snippets', 'list_sorted', {
          sortBy: this.sortBy,
          searchTerm: this.search,
          searchResults: this.total,
          sortOrder: this.sortDirection === SortDirection.Ascending ? 'Ascending' : 'Descending',
        });

        this.trackListSortedEvent = false;
      }

      return snippets;
    } catch (error: any) {
      this.apiErrors.report(error);
      return [];
    }
  });
}
